'use strict'

const { scopeCss } = require('./view-css')
const { relatedSpecFromKey, relatedViewColumnOptions } = require('./related-view-columns')

// A custom view is HTML the app owner writes. Field values are inserted as
// text. The live app and the designer both render through these functions.

const MAX_VIEW_CODE = 40000
const MAX_VIEW_CSS = 20000
const MAX_EACH = 500
const MAX_DEPTH = 20
const CUSTOM_VIEW_ROW_LIMIT = 100

const SKIPPED_COLUMN_TYPES = new Set(['password', 'image', 'file', 'relationship', 'signature'])

const SYSTEM_COLUMNS = [
  { uniqueTitle: 'id', title: 'ID', type: 'number' },
  { uniqueTitle: 'createdAt', title: 'Created at', type: 'dateTime' },
  { uniqueTitle: 'updatedAt', title: 'Updated at', type: 'dateTime' }
]

const CONTEXT_ONLY = new Set(['records', 'record', 'count', 'empty'])

const PATH = /^@?[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)*$/

const EXAMPLES = [
  {
    id: 'list',
    label: 'List',
    description: 'One block per record, with a heading and the next fields under it.'
  },
  {
    id: 'cards',
    label: 'Cards',
    description: 'Records sit side by side. Each card shows a heading and labeled fields.'
  },
  {
    id: 'detail',
    label: 'First record',
    description: 'Shows the first record as a labeled list. Useful when the page is about one row.'
  },
  {
    id: 'summary',
    label: 'Count and names',
    description: 'A count, then one line per record using the first field.'
  },
  {
    id: 'blank',
    label: 'Blank',
    description: 'A short starting point with the record count. Replace it with your own HTML.'
  }
]

const BASE_CSS = [
  '.custom-count {',
  '  font-weight: 600;',
  '  margin: 0 0 12px;',
  '}',
  '.custom-empty {',
  '  color: #5c5c5a;',
  '}'
].join('\n')

const LIST_CSS = [
  '.custom-record {',
  '  border: 1px solid #d7d7d4;',
  '  border-radius: 8px;',
  '  padding: 12px 14px;',
  '  margin: 0 0 10px;',
  '}',
  '.custom-record h2 {',
  '  margin: 0 0 4px;',
  '  font-size: 1.05rem;',
  '}',
  '.custom-record p {',
  '  margin: 0;',
  '}'
].join('\n')

const CARDS_CSS = [
  '.custom-grid {',
  '  display: flex;',
  '  flex-wrap: wrap;',
  '  gap: 12px;',
  '}',
  '.custom-card {',
  '  flex: 1 1 220px;',
  '  border: 1px solid #d7d7d4;',
  '  border-radius: 8px;',
  '  padding: 12px 14px;',
  '}',
  '.custom-card h2 {',
  '  margin: 0 0 8px;',
  '  font-size: 1.05rem;',
  '}',
  '.custom-card dl {',
  '  margin: 0;',
  '}',
  '.custom-card dt {',
  '  font-size: 0.75rem;',
  '  color: #5c5c5a;',
  '}',
  '.custom-card dd {',
  '  margin: 0 0 8px;',
  '}'
].join('\n')

function escapeHtml (value) {

  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function isPath (value) {

  return PATH.test(value)
}

function customViewColumns (columns) {

  return (columns || []).filter((column) => column && column.uniqueTitle && !SKIPPED_COLUMN_TYPES.has(column.type))
}

function customViewLimit (requested) {

  const number = Number(requested)

  if (!Number.isFinite(number) || number <= 0) return CUSTOM_VIEW_ROW_LIMIT

  return Math.min(Math.floor(number), CUSTOM_VIEW_ROW_LIMIT)
}

function plainCustomViewValue (value) {

  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'boolean') return value
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (value instanceof Date) return value.toISOString()

  return ''
}

function textWithoutMarkup (value) {

  if (typeof value !== 'string') return value
  if (/^\s*<img\b/i.test(value)) return ''

  return value
}

function plainCustomViewRows (rows) {

  return (Array.isArray(rows) ? rows : []).map((row) => {

    const plain = {}

    if (!row || typeof row !== 'object') return plain

    for (const key of Object.keys(row)) {

      if (key.charAt(0) === '_') continue
      if (key.endsWith('_filename')) continue

      const filename = row[`${key}_filename`]

      if (typeof filename === 'string' && filename.trim()) {

        plain[key] = filename.trim()

        continue
      }

      const value = plainCustomViewValue(row[key])

      plain[key] = typeof value === 'string' ? textWithoutMarkup(value) : value
    }

    return plain
  })
}

function customViewDomId (uniqueTitle) {

  const id = String(uniqueTitle || 'custom').replace(/[^A-Za-z0-9_-]/g, '')

  return id || 'custom'
}

function customViewStyleText (cssText) {

  return String(cssText || '').replace(/</g, '')
}

function customViewScopedCss (uniqueTitle, cssText) {

  const source = String(cssText || '').slice(0, MAX_VIEW_CSS)
  const scoped = scopeCss(`[data-view-id="${customViewDomId(uniqueTitle)}"]`, source)

  return customViewStyleText(scoped)
}

function fieldTag (column, prefix) {

  const name = prefix ? `${prefix}.${column.uniqueTitle}` : column.uniqueTitle

  return `{{${name}}}`
}

function listCode (fields) {

  if (!fields.length) {

    return [
      '<p class="custom-count">{{count}} records</p>',
      '{{#each records}}',
      '  <article class="custom-record">',
      '    <h2>Record {{@index}}</h2>',
      '  </article>',
      '{{else}}',
      '  <p class="custom-empty">No records yet.</p>',
      '{{/each}}'
    ].join('\n')
  }

  const lines = [
    '<p class="custom-count">{{count}} records</p>',
    '{{#each records}}',
    '  <article class="custom-record">',
    `    <h2>${fieldTag(fields[0])}</h2>`
  ]

  fields.slice(1, 3).forEach((field) => lines.push(`    <p>${fieldTag(field)}</p>`))

  lines.push('  </article>', '{{else}}', '  <p class="custom-empty">No records yet.</p>', '{{/each}}')

  return lines.join('\n')
}

function cardsCode (fields) {

  if (!fields.length) return listCode(fields)

  const lines = [
    '<div class="custom-grid">',
    '{{#each records}}',
    '  <article class="custom-card">',
    `    <h2>${fieldTag(fields[0])}</h2>`
  ]

  if (fields.length > 1) {

    lines.push('    <dl>')
    fields.slice(1, 4).forEach((field) => {

      lines.push(`      <dt>${escapeHtml(field.title || field.uniqueTitle)}</dt>`)
      lines.push(`      <dd>${fieldTag(field)}</dd>`)
    })
    lines.push('    </dl>')
  }

  lines.push('  </article>', '{{else}}', '  <p class="custom-empty">No records yet.</p>', '{{/each}}', '</div>')

  return lines.join('\n')
}

function detailCode (fields) {

  if (!fields.length) {

    return [
      '{{#if record}}',
      '  <p>Record {{record.id}}</p>',
      '{{else}}',
      '  <p class="custom-empty">No records yet.</p>',
      '{{/if}}'
    ].join('\n')
  }

  const lines = [
    '{{#if record}}',
    '  <article class="custom-detail">',
    `    <h2>${fieldTag(fields[0], 'record')}</h2>`,
    '    <dl>'
  ]

  fields.slice(0, 6).forEach((field) => {

    lines.push(`      <dt>${escapeHtml(field.title || field.uniqueTitle)}</dt>`)
    lines.push(`      <dd>${fieldTag(field, 'record')}</dd>`)
  })

  lines.push('    </dl>', '  </article>', '{{else}}', '  <p class="custom-empty">No records yet.</p>', '{{/if}}')

  return lines.join('\n')
}

function summaryCode (fields) {

  const item = fields.length ? fieldTag(fields[0]) : 'Record {{@index}}'

  return [
    '<p class="custom-count">{{count}} records</p>',
    '<ul class="custom-summary">',
    '{{#each records}}',
    `  <li>${item}</li>`,
    '{{else}}',
    '  <li class="custom-empty">No records yet.</li>',
    '{{/each}}',
    '</ul>'
  ].join('\n')
}

function blankCode () {

  return [
    '{{! Replace this note with your own HTML. }}',
    '<p class="custom-count">{{count}} records</p>',
    '{{#each records}}',
    '  <p>Record {{@index}}</p>',
    '{{/each}}'
  ].join('\n')
}

function exampleCode (id, fields) {

  if (id === 'cards') return cardsCode(fields)
  if (id === 'detail') return detailCode(fields)
  if (id === 'summary') return summaryCode(fields)
  if (id === 'blank') return blankCode()

  return listCode(fields)
}

function exampleCss (id) {

  if (id === 'cards') return `${BASE_CSS}\n${CARDS_CSS}`
  if (id === 'list' || id === 'detail') return `${BASE_CSS}\n${LIST_CSS}`

  return BASE_CSS
}

function customViewExamples (columns) {

  const fields = customViewColumns(columns)

  return EXAMPLES.map((example) => ({
    id: example.id,
    label: example.label,
    description: example.description,
    code: exampleCode(example.id, fields),
    css: exampleCss(example.id)
  }))
}

function customViewSyntax () {

  return [
    { label: '{{region}}', detail: 'Inserts that field as text. A value from a record cannot add HTML.' },
    { label: '{{rel.customer.city}}', detail: 'A field on a linked spreadsheet. rel. follows a link on this sheet. rev.orders.customer.city follows a link from the other sheet. The field buttons insert the exact tag.' },
    { label: '{{#each records}}...{{/each}}', detail: 'Repeats the inside once for every loaded record. Inside the loop, write the field name on its own, such as {{region}}.' },
    { label: '{{#if region}}...{{/if}}', detail: 'Shows the inside only when that field has a value. Zero and a blank string count as empty.' },
    { label: '{{else}}', detail: 'The part after {{else}} shows when {{#if}} is empty, or when {{#each}} has no records. Close the block after it.' },
    { label: '{{count}}', detail: 'How many records were loaded. Write it outside {{#each}}, because a column with the same name would hide it inside the loop.' },
    { label: '{{record.region}}', detail: 'A field from the first record, without looping.' },
    { label: '{{@index}}', detail: 'The record number inside {{#each}}, starting at 1.' },
    { label: '{{! note }}', detail: 'A comment for the next person who edits the view. Visitors do not see it.' }
  ]
}

function parseCustomView (template) {

  const source = String(template || '')
  const root = { type: 'root', children: [], elseChildren: null, inElse: false }
  const stack = [root]
  let index = 0

  function current () {

    return stack[stack.length - 1]
  }

  function list () {

    const block = current()

    return block.inElse ? block.elseChildren : block.children
  }

  while (index < source.length) {

    const open = source.indexOf('{{', index)

    if (open === -1) {

      if (index < source.length) list().push({ type: 'text', value: source.slice(index) })

      break
    }

    if (open > index) list().push({ type: 'text', value: source.slice(index, open) })

    const close = source.indexOf('}}', open + 2)

    if (close === -1) return { ok: false, error: 'A {{ tag was left unclosed.' }

    const raw = source.slice(open + 2, close).trim()

    index = close + 2

    if (raw.startsWith('!')) continue

    if (raw === 'else') {

      const block = current()

      if (block.type !== 'if' && block.type !== 'each') {

        return { ok: false, error: '{{else}} has to sit inside {{#if}} or {{#each}}.' }
      }

      if (block.inElse) return { ok: false, error: 'This block already has an {{else}}.' }

      block.inElse = true
      block.elseChildren = []

      continue
    }

    if (raw === '/each' || raw === '/if') {

      const expected = raw.slice(1)
      const block = stack.pop()

      if (!block || block.type !== expected || stack.length === 0) {

        return { ok: false, error: `{{/${expected}}} does not close a {{#${expected}}}.` }
      }

      list().push({
        type: block.type,
        path: block.path,
        children: block.children,
        elseChildren: block.elseChildren
      })

      continue
    }

    if (raw.startsWith('#each') || raw.startsWith('#if')) {

      const kind = raw.startsWith('#each') ? 'each' : 'if'
      const match = raw.match(kind === 'each' ? /^#each\s+(\S+)$/ : /^#if\s+(\S+)$/)

      if (!match || !isPath(match[1])) {

        const hint = kind === 'each' ? '{{#each records}}' : '{{#if region}}'

        return { ok: false, error: `{{#${kind}}} needs a name. ${hint} is the usual form.` }
      }

      if (stack.length > MAX_DEPTH) {

        return { ok: false, error: 'This view nests too many {{#each}} and {{#if}} blocks. Keep them within 20 levels.' }
      }

      stack.push({
        type: kind,
        path: match[1],
        children: [],
        elseChildren: null,
        inElse: false
      })

      continue
    }

    if (raw.startsWith('#') || raw.startsWith('/')) {

      return { ok: false, error: `Unknown tag {{${raw}}}. Use {{#each}}, {{#if}}, {{else}}, {{/each}}, {{/if}}, and field names.` }
    }

    if (!isPath(raw)) {

      return { ok: false, error: `{{${raw}}} is not a field name. Use letters, digits, and underscores, such as {{region}}.` }
    }

    list().push({ type: 'value', path: raw })
  }

  if (stack.length !== 1) {

    const open = stack[stack.length - 1]

    return { ok: false, error: `{{#${open.type} ${open.path}}} was not closed with {{/${open.type}}}.` }
  }

  return { ok: true, nodes: root.children }
}

function checkCustomView (template) {

  if (String(template || '').length > MAX_VIEW_CODE) {

    return { ok: false, error: `View code is limited to ${MAX_VIEW_CODE} characters.` }
  }

  return parseCustomView(template)
}

function customViewReady (template) {

  const checked = checkCustomView(template)

  if (!checked.ok) return checked

  if (!String(template || '').trim()) {

    return { ok: false, error: 'Write the view, or start from an example.' }
  }

  return checked
}

function relatedFieldName (path) {

  const head = String(path || '').split('.')[0]
  const rest = CONTEXT_ONLY.has(head) ? path.slice(head.length + 1) : path

  if (!rest.startsWith('rel.') && !rest.startsWith('rev.')) return null

  return relatedSpecFromKey(rest) ? rest : ''
}

function namesInPath (path) {

  if (!path || path.charAt(0) === '@') return []

  const related = relatedFieldName(path)

  if (related === '') return []
  if (related) return [related]

  const parts = path.split('.').filter((part) => part !== 'length' && part.charAt(0) !== '@')

  if (!parts.length) return []

  if (CONTEXT_ONLY.has(parts[0])) return parts.slice(1)

  return parts
}

function customViewRelatedSpecs (viewCode) {

  const specs = []

  for (const name of customViewFieldNames(viewCode)) {

    const spec = relatedSpecFromKey(name)

    if (spec) specs.push(spec)
  }

  return specs
}

function customViewInsertFields (spreadsheet, allSpreadsheets) {

  const local = customViewColumns(spreadsheet && spreadsheet.columns).concat(SYSTEM_COLUMNS).map((column) => ({
    uniqueTitle: column.uniqueTitle,
    title: column.title || column.uniqueTitle
  }))
  const related = relatedViewColumnOptions(spreadsheet, allSpreadsheets).map((option) => ({
    uniqueTitle: option.value,
    title: option.text
  }))

  return local.concat(related)
}

function customViewFieldNames (template) {

  const parsed = checkCustomView(template)

  if (!parsed.ok) return []

  const names = []
  const seen = new Set()

  function add (path) {

    for (const name of namesInPath(path)) {

      if (seen.has(name)) continue

      seen.add(name)
      names.push(name)
    }
  }

  function walk (nodes) {

    for (const node of nodes || []) {

      if (node.type === 'value' || node.type === 'if' || node.type === 'each') add(node.path)
      if (node.children) walk(node.children)
      if (node.elseChildren) walk(node.elseChildren)
    }
  }

  walk(parsed.nodes)

  return names
}

function lookup (context, path) {

  if (context == null || !path) return undefined

  if (typeof context === 'object' && Object.prototype.hasOwnProperty.call(context, path)) {

    return context[path]
  }

  const dot = path.indexOf('.')

  if (dot === -1) {

    if (path === 'length' && (Array.isArray(context) || typeof context === 'string')) return context.length
    if (typeof context !== 'object') return undefined

    return context[path]
  }

  const head = path.slice(0, dot)
  let next

  if (head === 'length' && (Array.isArray(context) || typeof context === 'string')) next = context.length
  else if (typeof context === 'object') next = context[head]
  else return undefined

  return lookup(next, path.slice(dot + 1))
}

function truthy (value) {

  if (Array.isArray(value)) return value.length > 0
  if (value === 0 || value === false) return false

  return value !== null && value !== undefined && value !== ''
}

function choiceList (value) {

  const source = Array.isArray(value)
    ? value
    : (typeof value === 'string' && value.trim().charAt(0) === '[' ? parsedChoices(value) : null)

  if (!source) return null

  return source
}

function parsedChoices (value) {

  try {

    const parsed = JSON.parse(value)

    return Array.isArray(parsed) ? parsed : null
  } catch (err) {

    return null
  }
}

function display (value) {

  const choices = choiceList(value)

  if (choices) {

    return choices.map((item) => {

      if (item == null || typeof item === 'object') return ''

      return display(item)
    }).filter((part) => part !== '').join(', ')
  }

  const plain = plainCustomViewValue(value)

  if (plain === true) return 'true'
  if (plain === false) return 'false'
  if (plain === 0) return '0'
  if (plain === '' || plain == null) return ''

  return String(plain)
}

function renderNodes (nodes, context) {

  let html = ''

  for (const node of nodes || []) {

    if (node.type === 'text') {

      html += node.value

      continue
    }

    if (node.type === 'value') {

      html += escapeHtml(display(lookup(context, node.path)))

      continue
    }

    if (node.type === 'if') {

      const branch = truthy(lookup(context, node.path)) ? node.children : (node.elseChildren || [])

      html += renderNodes(branch, context)

      continue
    }

    if (node.type === 'each') {

      const list = lookup(context, node.path)

      if (!Array.isArray(list) || list.length === 0) {

        html += renderNodes(node.elseChildren || [], context)

        continue
      }

      const cap = Math.min(list.length, MAX_EACH)

      for (let index = 0; index < cap; index += 1) {

        const item = list[index]
        const child = item && typeof item === 'object' && !Array.isArray(item)
          ? Object.assign(Object.create(context), item)
          : Object.assign(Object.create(context), { '.': item })

        child['@index'] = index + 1
        child['@first'] = index === 0
        child['@last'] = index === cap - 1
        html += renderNodes(node.children, child)
      }
    }
  }

  return html
}

function renderCustomView (template, context) {

  const checked = checkCustomView(template)

  if (!checked.ok) return { ok: false, error: checked.error, html: '' }

  return { ok: true, error: '', html: renderNodes(checked.nodes, context || {}) }
}

function customViewContext (records, title) {

  const list = plainCustomViewRows(records)

  return {
    title: title || '',
    count: list.length,
    empty: list.length === 0,
    records: list,
    record: list[0] || null
  }
}

function sampleValue (column, index) {

  const words = ['North', 'Harbor', 'Field']

  if (column.type === 'number') return (index + 1) * 12
  if (column.type === 'dateTime') return `2026-0${(index % 9) + 1}-15`
  if (column.type === 'multiselect') return index % 2 === 0 ? 'Red, Blue' : 'Green'

  return `${words[index % words.length]} ${column.title || column.uniqueTitle}`
}

function sampleRecords (columns) {

  const fields = customViewColumns(columns).concat(SYSTEM_COLUMNS.filter((column) => {

    return !customViewColumns(columns).some((existing) => existing.uniqueTitle === column.uniqueTitle)
  }))
  const rows = []

  for (let index = 0; index < 3; index += 1) {

    const row = { id: index + 1 }

    for (const column of fields) {

      if (column.uniqueTitle === 'id') continue

      row[column.uniqueTitle] = sampleValue(column, index)
    }

    rows.push(row)
  }

  return rows
}

function sampleCustomViewContext (columns, title) {

  return customViewContext(sampleRecords(columns), title)
}

function sanitizePreviewHtml (html) {

  return require('./html-sanitize').sanitizeHtml(html)
}

function filterFieldName (field) {

  if (!field || typeof field !== 'object') return ''

  if (field.source === 'forward' && field.relationshipColumn && field.field) {

    return `rel.${field.relationshipColumn}.${field.field}`
  }

  if (field.source === 'reverse' && field.sourceSpreadsheet && field.sourceRelationshipColumn && field.field) {

    return `rev.${field.sourceSpreadsheet}.${field.sourceRelationshipColumn}.${field.field}`
  }

  return typeof field.field === 'string' ? field.field : ''
}

function filterGroupsByNames (groups, names) {

  const allowed = names instanceof Set ? names : new Set(names || [])
  const kept = []

  for (const group of groups || []) {

    const fields = (group.fields || []).filter((field) => allowed.has(filterFieldName(field)))

    if (!fields.length) continue

    kept.push(Object.assign({}, group, { fields }))
  }

  return kept
}

function narrowUserFilters (filters, names, enabled) {

  if (!enabled) return []

  const allowed = names instanceof Set ? names : new Set(names || [])

  return (Array.isArray(filters) ? filters : []).filter((filter) => allowed.has(filterFieldName(filter)))
}

function sortsNamedInView (sorts, names) {

  const allowed = names instanceof Set ? names : new Set(names || [])

  return (Array.isArray(sorts) ? sorts : []).filter((sort) => sort && allowed.has(sort.column))
}

function customViewPreviewDocument (view, columns) {

  const ready = customViewReady(view && view.viewCode)

  if (!ready.ok) return { error: ready.error }

  const title = (view && view.title) || ''
  const related = customViewFieldNames(view.viewCode)
    .filter((name) => relatedSpecFromKey(name))
    .map((uniqueTitle) => ({
      uniqueTitle,
      title: (relatedSpecFromKey(uniqueTitle) || {}).relatedField || uniqueTitle,
      type: 'text'
    }))
  const context = Array.isArray(columns)
    ? sampleCustomViewContext([].concat(columns, related), title)
    : sampleContextForCode(view.viewCode, title)
  const rendered = renderCustomView(view.viewCode, context)

  if (!rendered.ok) return { error: rendered.error }

  const domId = customViewDomId(view && view.uniqueTitle)
  const css = require('./html-sanitize').sanitizeStyleText(customViewScopedCss(view && view.uniqueTitle, (view && view.viewCss) || ''))
  const body = sanitizePreviewHtml(rendered.html)
  const html = [
    '<!DOCTYPE html><html><head><meta charset="utf-8">',
    css ? `<style>${css}</style>` : '',
    '<style>body{margin:16px;background:#fff;color:#1c1c1a;font-family:sans-serif}h1,h2,h3,h4,p,li,dt,dd{color:#1c1c1a}</style>',
    `</head><body><div data-view-id="${domId}">${body}</div></body></html>`
  ].join('')

  return { html, text: body }
}

function sampleContextForCode (viewCode, title) {

  const columns = customViewFieldNames(viewCode).map((uniqueTitle) => ({
    uniqueTitle,
    title: uniqueTitle,
    type: uniqueTitle === 'id' ? 'number' : 'text'
  }))

  return sampleCustomViewContext(columns, title)
}

module.exports = {
  MAX_VIEW_CODE,
  MAX_VIEW_CSS,
  CUSTOM_VIEW_ROW_LIMIT,
  SYSTEM_COLUMNS,
  customViewColumns,
  customViewLimit,
  plainCustomViewValue,
  plainCustomViewRows,
  customViewDomId,
  customViewStyleText,
  customViewScopedCss,
  customViewExamples,
  customViewSyntax,
  checkCustomView,
  customViewReady,
  customViewFieldNames,
  customViewRelatedSpecs,
  customViewInsertFields,
  renderCustomView,
  customViewContext,
  sampleRecords,
  sampleCustomViewContext,
  sampleContextForCode,
  sanitizePreviewHtml,
  filterFieldName,
  filterGroupsByNames,
  narrowUserFilters,
  sortsNamedInView,
  customViewPreviewDocument
}
